/**
 * General Settings Page
 *
 * Platform-wide settings and preferences:
 * - Organization settings
 * - Notification preferences
 * - Default configurations
 */
import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Title2,
  Title3,
  Text,
  Card,
  Button,
  makeStyles,
  tokens,
  Field,
  Input,
  Switch,
  Divider,
  Spinner,
  MessageBar,
  MessageBarBody,
} from '@fluentui/react-components';
import {
  BuildingRegular,
  AlertRegular,
  ShieldCheckmarkRegular,
  SaveRegular,
  ArrowResetRegular,
} from '@fluentui/react-icons';
import { settingsApi, type TenantSettingsData } from '../services/api';
import { UnauthenticatedBanner } from '../components/common/UnauthenticatedBanner';
import { PageHeader } from '../components/common/PageHeader';

const useStyles = makeStyles({
  page: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXL,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
  },
  headerIcon: {
    fontSize: '32px',
    color: tokens.colorBrandForeground1,
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  sectionIcon: {
    fontSize: '24px',
    color: tokens.colorBrandForeground1,
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  formRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: tokens.spacingHorizontalL,
  },
  switchRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: `${tokens.spacingVerticalS} 0`,
  },
  switchLabel: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
  },
  switchDescription: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  actions: {
    display: 'flex',
    gap: tokens.spacingHorizontalM,
    marginTop: tokens.spacingVerticalL,
  },
  successMessage: {
    marginBottom: tokens.spacingVerticalM,
  },
});

export const GeneralSettingsPage: React.FC = () => {
  const styles = useStyles();
  const queryClient = useQueryClient();
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState<string | null>(null);

  // Fetch tenant and settings from API
  const { data: tenant, isLoading: loadingTenant, error: tenantError } = useQuery({
    queryKey: ['tenant', 'current'],
    queryFn: () => settingsApi.getCurrentTenant(),
  });

  const { data: settingsResponse, isLoading: loadingSettings, error: settingsError } = useQuery({
    queryKey: ['tenant', 'settings'],
    queryFn: () => settingsApi.getTenantSettings(),
  });

  // Local state for form editing
  const [organizationName, setOrganizationName] = useState('');
  const [notificationEmail, setNotificationEmail] = useState('');
  const [emailNotificationsEnabled, setEmailNotificationsEnabled] = useState(true);
  const [slackWebhookUrl, setSlackWebhookUrl] = useState('');
  const [requireMfa, setRequireMfa] = useState(false);
  const [mcpEnabled, setMcpEnabled] = useState(true);
  const [mcpRateLimitPerMinute, setMcpRateLimitPerMinute] = useState(100);
  const [evidenceRetentionDays, setEvidenceRetentionDays] = useState(365);
  const [auditLogRetentionDays, setAuditLogRetentionDays] = useState(730);

  // Initialize form state from API data
  useEffect(() => {
    if (tenant) {
      setOrganizationName(tenant.name || '');
    }
  }, [tenant]);

  useEffect(() => {
    if (settingsResponse?.settings) {
      const s = settingsResponse.settings;
      setNotificationEmail(s.notificationEmail || '');
      setEmailNotificationsEnabled(s.emailNotificationsEnabled);
      setSlackWebhookUrl(s.slackWebhookUrl || '');
      setRequireMfa(s.requireMfa);
      setMcpEnabled(s.mcpEnabled);
      setMcpRateLimitPerMinute(s.mcpRateLimitPerMinute);
      setEvidenceRetentionDays(s.evidenceRetentionDays);
      setAuditLogRetentionDays(s.auditLogRetentionDays);
    }
  }, [settingsResponse]);

  // Mutation for saving settings
  const saveMutation = useMutation({
    mutationFn: (settings: Partial<TenantSettingsData>) => settingsApi.updateTenantSettings(settings),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant'] });
      setShowSuccess(true);
      setShowError(null);
      setTimeout(() => setShowSuccess(false), 3000);
    },
    onError: (error: Error) => {
      setShowError(error.message);
      setTimeout(() => setShowError(null), 5000);
    },
  });

  const handleSave = () => {
    const updatedSettings: Partial<TenantSettingsData> = {
      notificationEmail,
      emailNotificationsEnabled,
      slackWebhookUrl: slackWebhookUrl || undefined,
      requireMfa,
      mcpEnabled,
      mcpRateLimitPerMinute,
      evidenceRetentionDays,
      auditLogRetentionDays,
    };
    saveMutation.mutate(updatedSettings);
  };

  const handleReset = () => {
    if (settingsResponse?.settings) {
      const s = settingsResponse.settings;
      setNotificationEmail(s.notificationEmail || '');
      setEmailNotificationsEnabled(s.emailNotificationsEnabled);
      setSlackWebhookUrl(s.slackWebhookUrl || '');
      setRequireMfa(s.requireMfa);
      setMcpEnabled(s.mcpEnabled);
      setMcpRateLimitPerMinute(s.mcpRateLimitPerMinute);
      setEvidenceRetentionDays(s.evidenceRetentionDays);
      setAuditLogRetentionDays(s.auditLogRetentionDays);
    }
  };

  if (loadingTenant || loadingSettings) {
    return (
      <div className={styles.page}>
        <Spinner label="Loading settings..." />
      </div>
    );
  }

  // Auth error detection
  const queryError = (tenantError || settingsError) as any;
  const isUnauth = queryError?.status === 401 || queryError?.message?.includes('MSAL');
  if (isUnauth) {
    return (
      <div className={styles.page}>
        <PageHeader title="General Settings" subtitle="Configure platform-wide settings and preferences" />
        <UnauthenticatedBanner feature="settings" />
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <PageHeader title="General Settings" subtitle="Configure platform-wide settings and preferences" />

      {/* Success Message */}
      {showSuccess && (
        <MessageBar intent="success" className={styles.successMessage}>
          <MessageBarBody>Settings saved successfully!</MessageBarBody>
        </MessageBar>
      )}

      {/* Error Message */}
      {showError && (
        <MessageBar intent="error" className={styles.successMessage}>
          <MessageBarBody>Error saving settings: {showError}</MessageBarBody>
        </MessageBar>
      )}

      {/* Organization Settings */}
      <Card>
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <BuildingRegular className={styles.sectionIcon} />
            <Title2>Organization</Title2>
          </div>

          <div className={styles.form}>
            <div className={styles.formRow}>
              <Field label="Organization Name">
                <Input
                  value={organizationName}
                  onChange={(_, data) => setOrganizationName(data.value)}
                  disabled
                />
              </Field>
              <Field label="Notification Email">
                <Input
                  type="email"
                  value={notificationEmail}
                  onChange={(_, data) => setNotificationEmail(data.value)}
                  placeholder="security@company.com"
                />
              </Field>
            </div>

            <Field label="Subscription Tier">
              <Input
                value={tenant?.subscription || 'Unknown'}
                disabled
              />
            </Field>

            <div className={styles.formRow}>
              <Field label="Evidence Retention (days)" hint="How long to keep enforcement evidence">
                <Input
                  type="number"
                  value={evidenceRetentionDays.toString()}
                  onChange={(_, data) => setEvidenceRetentionDays(parseInt(data.value) || 365)}
                />
              </Field>
              <Field label="Audit Log Retention (days)" hint="How long to keep audit logs">
                <Input
                  type="number"
                  value={auditLogRetentionDays.toString()}
                  onChange={(_, data) => setAuditLogRetentionDays(parseInt(data.value) || 730)}
                />
              </Field>
            </div>
          </div>
        </div>
      </Card>

      {/* Notification Settings */}
      <Card>
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <AlertRegular className={styles.sectionIcon} />
            <Title2>Notifications</Title2>
          </div>

          <div className={styles.form}>
            <Title3>Email Notifications</Title3>

            <div className={styles.switchRow}>
              <div className={styles.switchLabel}>
                <Text weight="semibold">Enable Email Notifications</Text>
                <Text className={styles.switchDescription}>Receive email alerts for security events and enforcement updates</Text>
              </div>
              <Switch
                checked={emailNotificationsEnabled}
                onChange={(_, data) => setEmailNotificationsEnabled(data.checked)}
              />
            </div>

            <Divider />

            <Title3>Slack Integration</Title3>

            <Field label="Slack Webhook URL" hint="Configure a Slack incoming webhook to receive notifications">
              <Input
                type="url"
                value={slackWebhookUrl}
                onChange={(_, data) => setSlackWebhookUrl(data.value)}
                placeholder="https://hooks.slack.com/services/..."
              />
            </Field>
          </div>
        </div>
      </Card>

      {/* Security Settings */}
      <Card>
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <ShieldCheckmarkRegular className={styles.sectionIcon} />
            <Title2>Security</Title2>
          </div>

          <div className={styles.form}>
            <div className={styles.switchRow}>
              <div className={styles.switchLabel}>
                <Text weight="semibold">Require Multi-Factor Authentication</Text>
                <Text className={styles.switchDescription}>All users must enable MFA to access the platform</Text>
              </div>
              <Switch
                checked={requireMfa}
                onChange={(_, data) => setRequireMfa(data.checked)}
              />
            </div>

            <Divider />

            <Title3>MCP (Model Context Protocol)</Title3>

            <div className={styles.switchRow}>
              <div className={styles.switchLabel}>
                <Text weight="semibold">Enable MCP</Text>
                <Text className={styles.switchDescription}>Allow AI-powered enforcement assistance</Text>
              </div>
              <Switch
                checked={mcpEnabled}
                onChange={(_, data) => setMcpEnabled(data.checked)}
              />
            </div>

            {mcpEnabled && (
              <Field label="Rate Limit (requests/minute)" hint="Maximum MCP requests per minute">
                <Input
                  type="number"
                  value={mcpRateLimitPerMinute.toString()}
                  onChange={(_, data) => setMcpRateLimitPerMinute(parseInt(data.value) || 100)}
                  disabled={settingsResponse?.capabilities.maxApiRateLimit === -1 ? false :
                    mcpRateLimitPerMinute >= (settingsResponse?.capabilities.maxApiRateLimit || 100)}
                />
              </Field>
            )}
          </div>
        </div>
      </Card>

      {/* Action Buttons */}
      <div className={styles.actions}>
        <Button
          appearance="primary"
          icon={<SaveRegular />}
          onClick={handleSave}
          disabled={saveMutation.isPending}
        >
          {saveMutation.isPending ? 'Saving...' : 'Save Changes'}
        </Button>
        <Button
          appearance="outline"
          icon={<ArrowResetRegular />}
          onClick={handleReset}
          disabled={saveMutation.isPending}
        >
          Reset
        </Button>
      </div>
    </div>
  );
};
